import { useEffect, useState, type FC, type FormEvent } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { loginLocal, register, signIn } from '@/lib/auth';

const GoogleLogo: FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29a7.19 7.19 0 0 1 0-4.58V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

const MicrosoftLogo: FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
    <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
  </svg>
);

type Mode = 'login' | 'register';

const LoginPage: FC = () => {
  const { continueAsGuest } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // The OAuth callback is a top-level navigation, so the backend reports failures by
  // redirecting back here with ?authError=… rather than a JSON body.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('authError') === 'email-unverified') {
      setError(
        "This account's email address isn't verified with the provider. Verify it there and try again, or create an account with email and password.",
      );
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await loginLocal(email, password);
      } else {
        await register(email, password, name);
      }
      // Cookie is now set -- reload so AuthProvider re-fetches /me and the gate opens.
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setSubmitting(false);
    }
  }

  function switchMode(next: Mode): void {
    setMode(next);
    setError('');
  }

  return (
    <div className="mg-login">
      <div className="mg-login-title">MotoGo</div>
      <div className="mg-login-subtitle">
        {mode === 'login' ? 'Ride together. Sign in to continue.' : 'Create an account'}
      </div>

      <form className="mg-login-form" onSubmit={handleSubmit}>
        {mode === 'register' && (
          <input
            className="mg-input"
            type="text"
            placeholder="Name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={256}
          />
        )}
        <input
          className="mg-input"
          type="email"
          placeholder="Email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          maxLength={320}
        />
        <input
          className="mg-input"
          type="password"
          placeholder="Password"
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={mode === 'register' ? 8 : 1}
          maxLength={72}
        />
        {mode === 'register' && (
          <input
            className="mg-input"
            type="password"
            placeholder="Confirm password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            maxLength={72}
          />
        )}
        <button type="submit" className="mg-btn mg-btn-primary" disabled={submitting}>
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </button>
        <div className="mg-login-error">{error}</div>
      </form>

      <div className="mg-divider">or</div>

      <div className="mg-oauth-row">
        <button className="mg-btn mg-btn-outline" onClick={() => signIn('google')}>
          <GoogleLogo />
          Continue with Google
        </button>
        <button className="mg-btn mg-btn-outline" onClick={() => signIn('microsoft')}>
          <MicrosoftLogo />
          Continue with Microsoft
        </button>
      </div>

      {mode === 'login' ? (
        <div className="mg-muted-text">
          Don&apos;t have an account?{' '}
          <button type="button" className="mg-link-btn" onClick={() => switchMode('register')}>
            Create one
          </button>
        </div>
      ) : (
        <div className="mg-muted-text">
          Already have an account?{' '}
          <button type="button" className="mg-link-btn" onClick={() => switchMode('login')}>
            Sign in
          </button>
        </div>
      )}

      <button type="button" className="mg-guest-btn" onClick={continueAsGuest}>
        Continue as guest
      </button>
    </div>
  );
};

export default LoginPage;
